import {
  CommerceStateSchema,
  type CommerceSettings,
  type CommerceState,
} from "@stackje/site-builder";
import { AssetRefSchema, type AssetRef } from "./contract";
export interface CommerceApi {
  get(siteId: string): Promise<CommerceState>;
  save(
    siteId: string,
    settings: CommerceSettings,
    rowVersion: number,
  ): Promise<CommerceState>;
  connect(siteId: string, country: string): Promise<string>;
  refresh(siteId: string): Promise<CommerceState>;
  upload(siteId: string, body: Blob): Promise<AssetRef>;
}
export function createHttpCommerceApi(
  fetcher: typeof fetch = (...args) => fetch(...args),
): CommerceApi {
  const root = (id: string) =>
    `/_builder/sites/${encodeURIComponent(id)}/shop/commerce`;
  async function call(path: string, body?: unknown): Promise<unknown> {
    const response = await fetcher(path, {
      credentials: "same-origin",
      cache: "no-store",
      method: body === undefined ? "GET" : "POST",
      headers:
        body === undefined
          ? {}
          : {
              "Content-Type":
                body instanceof Blob ? body.type : "application/json",
            },
      body:
        body === undefined
          ? undefined
          : body instanceof Blob
            ? body
            : JSON.stringify(body),
    });
    const result = (await response.json()) as { error?: string };
    if (!response.ok)
      throw new Error(result.error ?? "This didn't save. Try again.");
    return result;
  }
  return {
    async get(id) {
      return CommerceStateSchema.parse(await call(root(id)));
    },
    async save(id, settings, rowVersion) {
      return CommerceStateSchema.parse(
        await call(root(id), { settings, rowVersion }),
      );
    },
    async connect(id, country) {
      const value = (await call(`${root(id)}/connect`, { country })) as {
        url: string;
      };
      return value.url;
    },
    async refresh(id) {
      return CommerceStateSchema.parse(await call(`${root(id)}/refresh`, {}));
    },
    async upload(id, body) {
      const value = (await call(
        `/_builder/sites/${encodeURIComponent(id)}/uploads`,
        body,
      )) as { asset: unknown };
      return AssetRefSchema.parse(value.asset);
    },
  };
}
